import { mobile, notMobile, styled, useRemToPx } from '@obvio/app'
import { SvgArrow } from '@obvio/svg'
import { CarouselKeen, HoveringCursorButton } from '@obvio/ui'
import { useCallback } from 'react'

import { CenteredSection } from '../../CenteredSection'

import type { KeenSliderOptions, KeenSliderPlugin } from 'keen-slider/react'
import type { ReactElement, MouseEvent } from 'react'

export const CarouselDivider = styled.div`
  height: 1px;
  width: 100%;
  background: ${(theme) => theme.stroke};
`

export const CarouselImage = styled.div`
  width: 100%;
  height: 33.75rem;
  position: relative;

  @media ${mobile} {
    height: 9rem;
  }
`

export const CarouselSection = styled(CenteredSection)`
  width: 100vw;
  max-width: unset !important;
  .rc-tabs-nav {
    max-width: 75rem;
    align-self: center;
    width: 100%;
  }

  .keen-slider > div {
    min-width: 25.875rem;
  }

  .keen-slider:not([data-keen-slider-disabled]) .keen-slider__slide {
    width: unset !important;
  }

  @media ${mobile} {
    width: 100%;
  }
`
const StyledWindow = styled.div``

const innerPlugins: Record<
  'imgAutoHeight' | 'autoWidth',
  (...args: any[]) => KeenSliderPlugin
> = {
  autoWidth:
    (widths: number[]) =>
    ({ slides, update }) => {
      slides.forEach((slide, index) => {
        const width = widths[index]
        // eslint-disable-next-line no-param-reassign
        slide.style.minWidth = `${width}px`

        // eslint-disable-next-line no-param-reassign
        slide.style.maxWidth = `${width}px`
      })

      update()
    },
  imgAutoHeight:
    () =>
    // eslint-disable-next-line unicorn/consistent-function-scoping
    ({ slides, on }) => {
      function setImgHeights() {
        const heights = slides?.map((slide) => {
          const [imgWrap] = slide.children[0].children
          return imgWrap.clientHeight
        })
        const minHeight = Math.min(...heights)

        slides.forEach((slide) => {
          const [imgWrap] = slide.children[0].children
          ;(imgWrap as HTMLDivElement).style.height = `${minHeight}px`
        })
      }

      on('created', setImgHeights)
      on('optionsChanged', setImgHeights)
    },
}

type CarouselBaseProps<DataType> = {
  data: DataType[]
  options?: KeenSliderOptions
  plugins?: KeenSliderPlugin[]
  render(data: DataType): ReactElement
}

type EnhancedProps = {
  children: ReactElement
  next: (event: MouseEvent) => void
  previous: (event: MouseEvent) => void
}

function Enhanced({ previous, next, children }: EnhancedProps) {
  return (
    <HoveringCursorButton
      actionLeft={previous}
      actionRight={next}
      render={StyledWindow}
      renderCursor={SvgArrow}
    >
      {children}
    </HoveringCursorButton>
  )
}

export function CarouselBase<DataType>({
  data,
  options,
  plugins,
  render,
}: CarouselBaseProps<DataType>): ReactElement {
  const remToPx = useRemToPx()

  const enhance = useCallback(
    (props: EnhancedProps) => <Enhanced {...props} />,
    [],
  )

  return (
    <CarouselKeen
      data={data}
      plugins={plugins}
      render={render}
      hideArrows
      enhance={enhance}
      options={{
        defaultAnimation: {
          duration: 500,
        },
        breakpoints: {
          [mobile]: {
            drag: true,
            loop: true,
            slides: {
              perView: 1,
              spacing: remToPx(2),
            },
          },
          [notMobile]: {
            drag: false,
            loop: true,
            slides: {
              perView: 'auto',
              spacing: remToPx(2),
            },
          },
        },
        ...options,
      }}
    />
  )
}

CarouselBase.plugins = innerPlugins
